import type { FC, ReactNode } from 'react';
import { memo, useEffect, useState } from 'react';

import { Box } from '@mui/material';

import { COUNTRY_PATHS, MAP_HEIGHT, MAP_WIDTH } from 'src/tv/live/nordicOutlines';
import type { MapPlace } from 'src/tv/live/places';
import { TOWNS } from 'src/tv/live/places';
import { TV_COLORS } from 'src/tv/tvTheme';

const TILT = (50 * Math.PI) / 180;
const CAMERA_DISTANCE = 260;
const SLAB_DEPTH = 3;

export type Point = [number, number];

/** Map plane point (z up, in map units) to screen, with the north tilted away from the viewer. */
export const project = (x: number, y: number, z = 0): Point => {
  const dx = x - MAP_WIDTH / 2;
  const dy = y - MAP_HEIGHT / 2;
  const depth = dy * Math.sin(TILT) + z * Math.cos(TILT);
  const scale = CAMERA_DISTANCE / (CAMERA_DISTANCE - depth);

  return [dx * scale, (dy * Math.cos(TILT) - z * Math.sin(TILT)) * scale];
};

export const toPath = (points: Point[], isClosed = false) => `M${points.map(([x, y]) => `${x.toFixed(2)} ${y.toFixed(2)}`).join('L')}${isClosed ? 'Z' : ''}`;

const parseRings = (d: string): Point[][] => d
  .split('M')
  .filter(Boolean)
  .map((ring) => ring.replace('Z', '').split('L').map((pair) => pair.split(' ').map(Number) as Point));

const projectRings = (d: string, z: number) => parseRings(d).map((ring) => toPath(ring.map(([x, y]) => project(x, y, z)), true)).join('');

const corners = [project(0, 0), project(MAP_WIDTH, 0), project(0, MAP_HEIGHT), project(MAP_WIDTH, MAP_HEIGHT, -SLAB_DEPTH)];
const minX = Math.min(...corners.map(([x]) => x)) + 6;
const maxX = Math.max(...corners.map(([x]) => x)) - 6;
const minY = Math.min(...corners.map(([, y]) => y)) - 4;
const maxY = Math.max(...corners.map(([, y]) => y)) + 2;
const VIEW_BOX = `${minX} ${minY} ${maxX - minX} ${maxY - minY}`;

const COUNTRIES = [
  { id: 'sweden', d: COUNTRY_PATHS.sweden, top: '#1C3A56', side: '#0F2134', isHome: true },
  { id: 'norway', d: COUNTRY_PATHS.norway, top: '#1C3A56', side: '#0F2134', isHome: true },
  { id: 'finland', d: COUNTRY_PATHS.finland, top: '#13212F', side: '#0C1722', isHome: false },
  { id: 'denmark', d: COUNTRY_PATHS.denmark, top: '#13212F', side: '#0C1722', isHome: false },
].map((country) => ({ ...country, topPath: projectRings(country.d, 0), sidePath: projectRings(country.d, -SLAB_DEPTH) }));

export const groundCircle = (center: MapPlace, radius: number) => toPath(
  Array.from({ length: 28 }, (_, i) => {
    const angle = (i / 28) * Math.PI * 2;

    return project(center.x + Math.cos(angle) * radius, center.y + Math.sin(angle) * radius);
  }),
  true
);

/** The map never changes, so it is kept out of the per-frame re-render. */
const StaticMap = memo(() => {
  return (
    <>
      {COUNTRIES.map((country) => <path
        key={`${country.id}-side`}
        d={country.sidePath}
        fill={country.side}
      />)}
      {COUNTRIES.map((country) => (
        <path
          key={country.id}
          d={country.topPath}
          fill={country.top}
          stroke={country.isHome ? 'rgba(120, 180, 230, 0.55)' : 'rgba(120, 180, 230, 0.15)'}
          strokeWidth={0.25}
          strokeLinejoin="round"
        />
      ))}
      {TOWNS.map((town) => {
        const [x, y] = project(town.x, town.y);

        return <circle
          key={town.name}
          cx={x}
          cy={y}
          r={0.35}
          fill={TV_COLORS.muted}
          opacity={0.5}
        />;
      })}
    </>
  );
});

/**
 * Sweden and Norway as a tilted slab with an animation clock; `children` draws the live layers on it
 * (`CallLayer`, `VisitLayer`). Hand-rolled perspective in SVG: a few hundred points per frame do not need WebGL.
 */
export const MapFrame: FC<{ children: (now: number) => ReactNode }> = (props) => {
  const { children } = props;
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    let frame = requestAnimationFrame(function tick() {
      setNow(Date.now());
      frame = requestAnimationFrame(tick);
    });

    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <Box
      component="svg"
      viewBox={VIEW_BOX}
      sx={{ width: '100%', height: '100%', display: 'block' }}
    >
      <defs>
        <filter
          id="tv-map-glow"
          x="-50%"
          y="-50%"
          width="200%"
          height="200%"
        >
          <feGaussianBlur stdDeviation={0.8} />
        </filter>
      </defs>
      <StaticMap />
      {children(now)}
    </Box>
  );
};
